/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'umss-switch',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssSwitchComponent),
      multi: true
    }
  ]
})
export class UmssSwitchComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  @Input() label?: string;
  @Input() ariaLabel?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;

  /** Value prop */
  private _checked: boolean = false;
  @Input() set checked(val: boolean) {
    this._checked = val;
    this._onChange(val);
  }
  get checked(): boolean {
    return this._checked;
  }

  /** Event outputs */
  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<boolean>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: boolean): void {
    this._checked = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onToggle(): void {
    if (this.disabled) return;

    this._checked = !this._checked;
    this._onChange(this._checked);
    this._onTouched();
    this.checkedChange.emit(this._checked);
    this.change.emit(this._checked);
  }

  onFocusEvent(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onBlurEvent(): void {
    if (this.disabled) return;
    this._onTouched();
    this.onBlur.emit();
  }

  /** Computed properties */
  get computedAriaLabel(): string {
    return this.ariaLabel || this.label || 'Toggle switch';
  }
}
